var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Typography, styled } from '@mui/material';
import { isNumber } from 'lodash';
import moment, { isMoment } from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import yup from '../../config/yup';
import { PlanType } from '../../enum';
import { useTerminologyTranslation } from '../app/terminology';
import { Autocomplete, FormField, QueueCheckboxGroup, StyledDatePicker } from '../common';
import { CUSTOM_WIDTH_AUTOCOMPLETE, DATE_PICKER_FORMAT, DATE_PICKER_SLASH_FORMAT, LIMIT_TIMELINE, MAX_QUEUES, PLAN_INTERVALS, SECTION_DATA } from './constants';
import { DAILY_FILTER_KEYS, FREQUENCY_ENUM, PERIOD_KEY, SECTION_ENUM } from './enum';
import { useQueuesAPI, useQueuesDropdown, useReportTimelineOptions, useScenarioDropdown, useTimelineSync } from './selectors';
export var StyledDialog = styled(Dialog)(function () {
  return {
    '& .MuiDialog-paper': {
      width: '36rem',
      maxWidth: '36rem'
    }
  };
});
export var StyledDialogTitle = styled(DialogTitle)(function (_a) {
  var theme = _a.theme;
  return {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main
  };
});
export var StyledCloseIcon = styled(FontAwesomeIcon)(function (_a) {
  var theme = _a.theme;
  return {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main
  };
});
export var StyledIconButton = styled(IconButton)(function (_a) {
  var theme = _a.theme;
  return {
    position: 'absolute',
    right: theme.spacing(4),
    top: theme.spacing(4),
    width: '2.25rem'
  };
});
var StyledBox = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(5)
  };
});
var StyledBoxFilters = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(5)
  };
});
var StyledTypography = styled(Typography)(function (_a) {
  var theme = _a.theme;
  return {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 'normal'
  };
});
var StyledRadioGroup = styled(RadioGroup)(function (_a) {
  var theme = _a.theme;
  return {
    flexDirection: 'row',
    gap: theme.spacing(3)
  };
});
var StyledDialogContent = styled(DialogContent)(function () {
  return {
    borderBottom: 'none'
  };
});
var StyledDialogActions = styled(DialogActions)(function () {
  return {
    padding: '1.5rem',
    justifyContent: 'space-between'
  };
});
var StyledExportButton = styled(Button)(function () {
  return {
    minWidth: '6.375rem'
  };
});
export var RadioGroupField = React.memo(function (_a) {
  var options = _a.options,
    value = _a.value,
    onChange = _a.onChange;
  return _jsx(StyledRadioGroup, __assign({
    value: value !== null && value !== void 0 ? value : '',
    onChange: function (e) {
      return onChange(e.target.value);
    }
  }, {
    children: options.map(function (opt) {
      return _jsx(FormControlLabel, {
        value: opt.value,
        control: _jsx(Radio, {}),
        label: opt.label
      }, opt.value);
    })
  }));
});
export var validateForm = yup.object().shape({
  planId: yup.number().optional(),
  queues: yup.array().of(yup.number()).min(1).required(),
  category: yup.string().required(),
  viewType: yup.string().required(),
  periodStart: yup.number().required(),
  periodEnd: yup.number().required(),
  filterDate: yup.mixed().when('viewType', function (_a, schema) {
    var viewType = _a[0];
    return viewType === DAILY_FILTER_KEYS.SPECIFIC_DAY ? schema.required() : schema.nullable();
  })
});
export var ExportDialog = function (_a) {
  var open = _a.open,
    planType = _a.planType,
    onClose = _a.onClose,
    onSubmit = _a.onSubmit;
  var t = useTerminologyTranslation('report', {
    keyPrefix: 'actual'
  }).t;
  var basePlanId = useParams().basePlanId;
  var initialValues = useMemo(function () {
    return {
      planId: Number(basePlanId),
      queues: [],
      category: SECTION_ENUM.PERFORMANCE,
      viewType: FREQUENCY_ENUM.PERIOD,
      periodStart: 0,
      periodEnd: 0
    };
  }, [basePlanId]);
  var methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validateForm)
  });
  var control = methods.control,
    getValues = methods.getValues,
    watch = methods.watch,
    setValue = methods.setValue,
    reset = methods.reset,
    handleSubmit = methods.handleSubmit,
    isValid = methods.formState.isValid;
  var _b = watch(),
    planId = _b.planId,
    viewType = _b.viewType,
    periodStart = _b.periodStart,
    periodEnd = _b.periodEnd,
    filterDate = _b.filterDate;
  var _c = useQueuesAPI(planId),
    fetchQueues = _c[1];
  var scenarioOptions = useScenarioDropdown();
  var queuesOptions = useQueuesDropdown(planId, false);
  var timelines = useReportTimelineOptions();
  var prevQueueIndicesRef = useRef([]);
  useEffect(function () {
    if (open) {
      fetchQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQueues, open]);
  useEffect(function () {
    var _a, _b;
    if (timelines === null || timelines === void 0 ? void 0 : timelines.length) {
      setValue('periodEnd', (_b = (_a = timelines.at(-1)) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelines.length, setValue]);
  useEffect(function () {
    if (prevQueueIndicesRef.current.length > 0 && queuesOptions.length > 0) {
      var newSelectedQueues = prevQueueIndicesRef.current.map(function (idx) {
        var _a;
        return (_a = queuesOptions[idx]) === null || _a === void 0 ? void 0 : _a.uniqueId;
      });
      setValue('queues', newSelectedQueues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuesOptions]);
  var isMonthlyPlanType = useMemo(function () {
    return [PlanType.Monthly, PlanType.MonthlyCustom].includes(planType);
  }, [planType]);
  var isShowEndTimeLine = useMemo(function () {
    return isMonthlyPlanType && viewType === FREQUENCY_ENUM.PERIOD || planType === PlanType.Weekly;
  }, [planType, viewType, isMonthlyPlanType]);
  var categoryOptions = useMemo(function () {
    return SECTION_DATA.map(function (item) {
      return {
        value: item.value,
        label: t("exportDialog.".concat(item.value))
      };
    });
  }, [t]);
  var getIntervalKey = useCallback(function (item) {
    if (item === FREQUENCY_ENUM.PERIOD) {
      return isMonthlyPlanType ? 'monthly' : 'weekly';
    }
    return item;
  }, [isMonthlyPlanType]);
  var planningIntervalOptions = useMemo(function () {
    return PLAN_INTERVALS.map(function (item) {
      return {
        value: item,
        label: t("exportDialog.".concat(getIntervalKey(item)))
      };
    });
  }, [t, getIntervalKey]);
  var isLimitedTimeline = useMemo(function () {
    return planType === PlanType.Weekly && viewType === FREQUENCY_ENUM.DAILY;
  }, [planType, viewType]);
  var startLimitDate = useMemo(function () {
    var _a;
    return moment((_a = timelines[0]) === null || _a === void 0 ? void 0 : _a.moment);
  }, [timelines]);
  var endLimitDate = useMemo(function () {
    var _a;
    var endDateSelected = (_a = timelines.at(-1)) === null || _a === void 0 ? void 0 : _a.moment;
    if (planType === PlanType.Weekly) {
      return moment(endDateSelected).add(6, 'day');
    }
    return moment(endDateSelected).add(1, 'month').subtract(1, 'day');
  }, [planType, timelines]);
  var handleTimelineChange = useTimelineSync({
    timelines: timelines,
    periodStart: periodStart,
    periodEnd: periodEnd,
    isLimitedTimeline: isLimitedTimeline,
    isShowEndTimeLine: isShowEndTimeLine,
    setValue: setValue
  });
  useEffect(function () {
    var _a;
    var startMoment = (_a = timelines === null || timelines === void 0 ? void 0 : timelines.find(function (item) {
      return item.id === periodStart;
    })) === null || _a === void 0 ? void 0 : _a.moment;
    if (!filterDate && isMoment(startMoment)) {
      setValue('filterDate', moment(startMoment));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, periodStart, timelines, setValue, t]);
  var handleClose = function () {
    onClose();
    reset(__assign(__assign({}, initialValues), {
      periodEnd: timelines.length - 1
    }));
    prevQueueIndicesRef.current = [];
  };
  var handleFormSubmit = function (data) {
    var _a, _b;
    var timelineEnd = (_b = (_a = timelines.at(-1)) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 0;
    var period_start = data.periodStart;
    var period_end = isShowEndTimeLine ? data.periodEnd : data.periodStart;
    if (viewType === DAILY_FILTER_KEYS.SPECIFIC_DAY) {
      period_start = 0;
      period_end = isShowEndTimeLine ? timelineEnd : data.periodStart;
    }
    var params = __assign({
      plan_id: Number(data.planId),
      queues: data.queues,
      category: data.category,
      view_type: data.viewType,
      period_start: period_start,
      period_end: period_end
    }, data.filterDate && {
      filter_date: data.filterDate.format(DATE_PICKER_SLASH_FORMAT)
    });
    onSubmit(params);
    handleClose();
  };
  var handleChangePlan = function () {
    prevQueueIndicesRef.current = getValues('queues').map(function (id) {
      return queuesOptions.findIndex(function (q) {
        return q.uniqueId === id;
      });
    });
  };
  return _jsx(FormProvider, __assign({}, methods, {
    children: _jsx(StyledDialog, __assign({
      open: open,
      onClose: handleClose,
      fullWidth: true,
      maxWidth: "md"
    }, {
      children: _jsxs(Box, __assign({
        component: "form",
        onSubmit: handleSubmit(handleFormSubmit)
      }, {
        children: [_jsx(StyledDialogTitle, {
          children: t('exportDialog.export')
        }), _jsx(StyledIconButton, __assign({
          "aria-label": "close",
          onClick: handleClose
        }, {
          children: _jsx(StyledCloseIcon, {
            icon: faXmark
          })
        })), _jsxs(StyledDialogContent, __assign({
          dividers: true
        }, {
          children: [_jsxs(StyledBox, {
            children: [_jsx(StyledTypography, {
              children: t('exportDialog.scenario')
            }), _jsx(FormField, __assign({
              required: true,
              name: "planId",
              control: control
            }, {
              children: _jsx(Autocomplete, {
                id: "planId",
                sx: {
                  minWidth: '8.5rem'
                },
                disablePortal: true,
                options: scenarioOptions,
                getOptionValue: function (o) {
                  return o.uniqueId;
                },
                onChange: handleChangePlan
              })
            }))]
          }), _jsxs(StyledBox, {
            children: [_jsx(StyledTypography, {
              children: t('exportDialog.queueLimitSelect')
            }), _jsx(Controller, {
              name: "queues",
              control: control,
              render: function (_a) {
                var field = _a.field;
                return _jsx(QueueCheckboxGroup, {
                  options: queuesOptions.filter(function (queue) {
                    return queue.isWat;
                  }),
                  value: (field.value || []).filter(isNumber),
                  onChange: field.onChange,
                  maxSelected: MAX_QUEUES,
                  labels: {
                    search: t('exportDialog.queueSearch')
                  },
                  ariaLabel: t('exportDialog.queueLimitSelect')
                });
              }
            })]
          }), _jsxs(StyledBox, {
            children: [_jsx(StyledTypography, {
              children: t('exportDialog.categories')
            }), _jsx(Controller, {
              name: "category",
              control: control,
              render: function (_a) {
                var field = _a.field;
                return _jsx(RadioGroupField, {
                  options: categoryOptions,
                  value: field.value,
                  onChange: field.onChange
                });
              }
            })]
          }), _jsxs(StyledBox, {
            children: [_jsx(StyledTypography, {
              children: t('exportDialog.planningInterval')
            }), _jsx(Controller, {
              name: "viewType",
              control: control,
              render: function (_a) {
                var field = _a.field;
                return _jsx(RadioGroupField, {
                  options: planningIntervalOptions,
                  value: field.value,
                  onChange: field.onChange
                });
              }
            })]
          }), _jsxs(StyledBoxFilters, {
            children: [_jsx(StyledTypography, {
              children: t('exportDialog.date')
            }), viewType === DAILY_FILTER_KEYS.SPECIFIC_DAY ? _jsx(FormField, __assign({
              required: true,
              name: "filterDate",
              control: control
            }, {
              children: _jsx(StyledDatePicker, {
                minDate: startLimitDate,
                maxDate: endLimitDate,
                control: control,
                format: DATE_PICKER_FORMAT
              })
            })) : _jsxs(Stack, __assign({
              direction: "row",
              alignItems: "center",
              gap: 2,
              flex: 1
            }, {
              children: [_jsx(FormField, __assign({
                required: true,
                name: "periodStart",
                control: control
              }, {
                children: _jsx(Autocomplete, {
                  id: PERIOD_KEY.START,
                  sx: {
                    minWidth: CUSTOM_WIDTH_AUTOCOMPLETE[planType]
                  },
                  options: timelines,
                  getOptionValue: function (o) {
                    return o.id;
                  },
                  onChange: function (id) {
                    return handleTimelineChange(PERIOD_KEY.START, id);
                  }
                })
              })), _jsx(Typography, __assign({
                fontSize: "0.875rem",
                sx: {
                  display: isShowEndTimeLine ? 'block' : 'none'
                }
              }, {
                children: t('to')
              })), _jsx(FormField, __assign({
                required: true,
                name: "periodEnd",
                control: control,
                sx: {
                  display: isShowEndTimeLine ? 'block' : 'none'
                }
              }, {
                children: _jsx(Autocomplete, {
                  id: PERIOD_KEY.END,
                  sx: {
                    minWidth: CUSTOM_WIDTH_AUTOCOMPLETE[planType]
                  },
                  options: timelines,
                  getOptionValue: function (o) {
                    return o.id;
                  },
                  onChange: function (id) {
                    return handleTimelineChange(PERIOD_KEY.END, id);
                  }
                })
              }))]
            })), _jsx(Typography, __assign({
              sx: {
                visibility: isLimitedTimeline ? 'visible' : 'hidden'
              }
            }, {
              children: t('helpDescription', {
                limit: LIMIT_TIMELINE
              })
            }))]
          })]
        })), _jsxs(StyledDialogActions, {
          children: [_jsx(Button, __assign({
            onClick: handleClose,
            variant: "outlined",
            sx: {
              minWidth: '6rem'
            }
          }, {
            children: t('exportDialog.cancel')
          })), _jsx(StyledExportButton, __assign({
            type: "submit",
            variant: "contained",
            disabled: !isValid
          }, {
            children: t('exportDialog.export')
          }))]
        })]
      }))
    }))
  }));
};